import React, { useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, ArrowRight } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { Section, Card, CheckList, Steps, FaqList, PageHero } from "../components/landing/Blocks";
import { AuditRequestForm } from "../components/landing/AuditRequestForm";
import { audit as c, FIX_PRICES } from "../data/landingPages";
import { applySeo } from "../lib/seo";

const PriceTable = () => (
  <div className="overflow-x-auto rounded-md border" style={{ borderColor: "var(--ggm-border-subtle)" }}>
    <table className="w-full text-left font-body text-sm md:text-base" data-testid="audit-price-table">
      <thead style={{ background: "var(--ggm-surface)" }}>
        <tr className="font-condensed uppercase tracking-widest text-xs text-white/60">
          <th className="p-4">Option</th><th className="p-4">Price</th><th className="p-4">What it's for</th>
        </tr>
      </thead>
      <tbody>
        {FIX_PRICES.map((r) => (
          <tr key={r.name} className="border-t" style={{ borderColor: "var(--ggm-border-subtle)" }}>
            <td className="p-4 text-white font-bold">{r.name}</td>
            <td className="p-4 text-[var(--ggm-signal-cyan)] whitespace-nowrap">{r.price}</td>
            <td className="p-4 text-white/75 min-w-[220px]">
              {r.use}
              {r.to && (
                <Link to={r.to} className="ml-2 underline text-white hover:text-[var(--ggm-signal-cyan)]" data-testid="audit-price-front-office-link">
                  Learn more
                </Link>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CaseStudy = () => (
  <Section id="example" eyebrow="A real example" title="Family Health Care of Gallatin" testId="audit-case-study">
    <p className="font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">{c.caseIntro}</p>
    <div className="mt-8 grid gap-6 md:grid-cols-2">
      <Card><h3 className="font-condensed uppercase text-white text-lg mb-4">What was already working</h3><CheckList items={c.working} /></Card>
      <Card><h3 className="font-condensed uppercase text-white text-lg mb-4">What the audit found</h3><CheckList items={c.found} /></Card>
    </div>
    <p className="mt-6 font-body text-white/80 max-w-3xl"><b className="text-white">The ranked fixes:</b> {c.rankedFixes}</p>
    {c.caseAfter && (
      <Card className="mt-6" testId="audit-case-after">
        <div className="ggm-eyebrow mb-2">After · {c.caseAfter.date}</div>
        <p className="font-body text-white/85">{c.caseAfter.text}</p>
        <Link to={c.caseAfter.to} className="mt-3 inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-sm text-[var(--ggm-signal-cyan)]" data-testid="audit-case-after-link">
          Read the full case study <ArrowRight size={16} />
        </Link>
      </Card>
    )}
    <Link to="/projects/family-health-care-of-gallatin" className="ggm-btn-secondary mt-6" data-testid="audit-case-study-link">
      See the project <ArrowRight size={16} />
    </Link>
  </Section>
);

export default function AuditPage() {
  const pinged = useRef(false);
  useEffect(() => applySeo(c), []);
  useEffect(() => {
    if (pinged.current) return;
    pinged.current = true;
    const src = new URLSearchParams(window.location.search).get("src");
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/track/robert-scan`, {
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        source: src === "print-qr" ? "audit-print-qr" : "audit-visit",
      })
      .catch(() => {});
  }, []);

  return (
    <PageShell testId="audit-page">
      <PageHero eyebrow="Free · Gallatin, TN · Sumner County" h1={c.h1} lede={c.lede} badges={c.badges} testId="audit-hero">
        <a href="#request" className="ggm-btn-primary" data-testid="audit-hero-cta">Get my free audit <ArrowUpRight size={16} /></a>
        <a href="#example" className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-sm text-white/80 hover:text-[var(--ggm-signal-cyan)]" data-testid="audit-hero-example-link">
          See a real one <ArrowDown size={16} />
        </a>
      </PageHero>

      <Section eyebrow="Three areas" title="What the free audit covers" testId="audit-covers">
        <div className="grid gap-6 md:grid-cols-3">
          {c.covers.map((x, i) => (
            <Card key={x.t} testId={`audit-cover-${i + 1}`}>
              <div className="font-condensed text-[var(--ggm-signal-cyan)] text-3xl">0{i + 1}</div>
              <h3 className="mt-3 font-condensed uppercase text-white text-xl">{x.t}</h3>
              <p className="mt-3 font-body text-white/75 leading-relaxed">{x.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section eyebrow="The report" title="What you get" testId="audit-gets">
        <p className="font-body text-white/75 mb-6">A short, branded report (not a 40-page PDF nobody reads):</p>
        <CheckList items={c.gets} />
      </Section>

      <CaseStudy />

      <Section eyebrow="Four steps" title="How it works" testId="audit-steps"><Steps steps={c.steps} testId="audit-step" /></Section>

      <Section eyebrow="Who it's for" title="Built for local business owners" testId="audit-who">
        <p className="font-body text-white/80 max-w-3xl mb-6">{c.whoIntro}</p>
        <CheckList items={c.whoFit} />
        <p className="mt-6 font-body text-white/65">{c.whoRemote}</p>
      </Section>

      <Section eyebrow="Fixed prices" title="If you want help with the fixes" testId="audit-prices">
        <p className="font-body text-white/75 mb-6">Every price is fixed and published up front:</p>
        <PriceTable />
      </Section>

      <Section eyebrow="Local builder" title="Why a local builder?" testId="audit-why">
        <p className="font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">{c.why}</p>
      </Section>

      <Section eyebrow="Questions, answered" title="FAQ" testId="audit-faq"><FaqList faqs={c.faqs} testId="audit-faq-list" /></Section>

      <Section id="request" eyebrow="Request your free Local AI Audit" title="Find out where you're losing customers, for free." testId="audit-request">
        <Card><AuditRequestForm replyNote={c.replyNote} /></Card>
      </Section>
    </PageShell>
  );
}
